import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Product : famille d'entités "élément de catalogue".
 *
 * Construite sur le PATRON asset (modèle "famille + type" de la bible). Le socle
 * ne connaît PAS d'article concret (une coupe, un abonnement, un menu) : il
 * connaît la famille générique `product`, et le vocabulaire métier est porté par
 * le champ `type` (chaîne libre déclarée par l'app : "bien", "service",
 * "formule"...). Un produit décrit UNE seule fois un bien, un service, une
 * prestation ou une formule composée, avec son prix de base et ses variantes.
 *
 * Périmètre v0 (org-wide, même doctrine qu'asset) : rien de sensible dans cette
 * famille. Les liens vers d'autres familles restent de simples ids (pas
 * d'import d'un autre schéma de famille).
 *
 * Convention de patron (cf. asset) :
 *   - `productCoreFields`        : champs MÉTIER seuls (sans les champs standard).
 *   - `ProductSchema`            : famille complète = champs standard + métier.
 *   - `ProductCreateInputSchema` : entrée fournie par l'appelant à la création
 *     (les champs standard sont remplis par le SDK). Valide AVANT écriture.
 */

/** Champs métier de la famille product (hors champs standard du socle). */
export const productCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "bien", "service", "formule"). */
  type: z.string().min(1),
  /** Nom d'affichage de l'élément de catalogue (ex. "Coupe homme"). */
  name: z.string().min(1),
  /** Code article / SKU (référence catalogue), optionnel. */
  sku: z.string().min(1).optional(),
  /** Prix de base hors remise (montant), optionnel. */
  basePrice: z.number().optional(),
  /** Taux de taxe applicable (ex. 0.17 pour 17%), optionnel. */
  taxRate: z.number().optional(),
  /** Unité de vente ou de mesure (pièce, heure, kg...), optionnel. */
  unit: z.string().min(1).optional(),
  /** Variantes : liste structurée pilotée par l'app (taille, couleur, options...), optionnel. */
  variants: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Suivi de stock activé pour ce produit, optionnel. */
  trackStock: z.boolean().optional(),
  /** Durée d'une prestation en minutes (cas service), optionnel. */
  durationMinutes: z.number().int().optional(),
  /** Tarif par défaut rattaché au produit (référence `price-list`), optionnel. */
  priceListId: z.string().min(1).optional(),
  /** Composition d'une formule (référence `composition`), optionnel. */
  compositionId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille product (champs standard + champs métier). */
export const ProductSchema = z.object({
  ...baseEntityFields,
  ...productCoreFields,
});

/** Type d'un product. */
export type Product = z.infer<typeof ProductSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ProductCreateInputSchema = z.object({
  ...productCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un product. */
export type ProductCreateInput = z.infer<typeof ProductCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const ProductUpdateInputSchema = z
  .object({ ...productCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un product. */
export type ProductUpdateInput = z.infer<typeof ProductUpdateInputSchema>;
